import PropTypes, { Validator } from 'prop-types';
import { ReactElement, Ref, RefAttributes, WeakValidationMap } from 'react';
import { GroupBase } from 'react-select';
import Select from 'react-select/dist/declarations/src/Select';
import Multiselect, { MultiselectOption, StructuredOption, AnySelectBaseProps } from '../Multiselect';
import { MultiselectProps } from '../Multiselect/Multiselect';
import { optionalBoolValidator, optionalStringValidator } from '../utils/betterPropTypes';
import forwardRefToProps from '../utils/forwardRefToProps';

export type SingleSelectProps<Option extends MultiselectOption> = AnySelectBaseProps<Option, false>;

export type SingleSelectRef = Select<StructuredOption, false, GroupBase<StructuredOption>>;

export interface SingleSelectComponentProps<Option extends MultiselectOption>
  extends AnySelectBaseProps<Option, false> {
  forwardedRef?: Ref<SingleSelectRef>;
}

/** Core implementation of SingleSelect. Forwards props to Multiselect. */
function SingleSelectComponent<Option extends MultiselectOption>({
  closeMenuOnSelect = true,
  forwardedRef,
  ...passthroughProps
}: SingleSelectComponentProps<Option>): ReactElement {
  /** Multiselect is overloaded, but the SingleSelectComponent implementation is
   * working with generic types. Do a type assertion to get multiselectProps to
   * conform to one of the overloaded function signatures. */
  const multiselectProps = passthroughProps as MultiselectProps<StructuredOption, false>;
  return <Multiselect isMulti={false} closeMenuOnSelect={closeMenuOnSelect} ref={forwardedRef} {...multiselectProps} />;
}

/** The final type of the SingleSelect default export.
 *
 * Supports options of type string[] or StructuredOption[], but not an array with
 * a mixture of both.
 */
export type SingleSelectType = {
  (props: SingleSelectProps<string> & RefAttributes<SingleSelectRef>): ReactElement;
  (props: SingleSelectProps<StructuredOption> & RefAttributes<SingleSelectRef>): ReactElement;
};
SingleSelectComponent.displayName = 'SingleSelect';

const singleSelectPropsTypes: WeakValidationMap<
  SingleSelectComponentProps<MultiselectOption> & { forwardedRef: Ref<SingleSelectRef>; '...rest': unknown }
> = {
  /**
   * Aria label for the input.
   * If not used in `FormField`, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required
   * for accessibility.
   */
  'aria-label': PropTypes.string,
  /**
   * HTML ID of an element that should be used as the label for the input. Will override `aria-label` if provided.
   * If not used in FormField, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required
   * for accessibility.
   */
  'aria-labelledby': PropTypes.string,
  /** If truthy, loaded data will be cached. The cache will remain until `cacheOptions` changes value. */
  cacheOptions: PropTypes.bool,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Close the select menu when the user selects an option */
  closeMenuOnSelect: PropTypes.bool,
  /** Determines the initial set of selected options, but allows them to be updated internally ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)) */
  defaultValue: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
    ]).isRequired
  ),
  /** Impacts the styling of the SingleSelect in addition to preventing selection */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling */
  hideRequiredStyles: PropTypes.bool,
  /** Adds an id to the root element of the component. To add an id to the `<input>` element, use the `inputId` prop */
  id: PropTypes.string,
  /**
   * Will not be used if `options` are provided and component is being used synchronously.
   * The default set of options to show in the menu before the user starts searching.
   * When set to true, the results for `loadAsyncOptions('')` will be loaded on mount.
   */
  initialAsyncOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
      PropTypes.shape({
        label: optionalStringValidator,
        options: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string.isRequired,
            PropTypes.shape({
              value: PropTypes.string.isRequired,
              disabled: optionalBoolValidator,
              label: optionalStringValidator,
            }).isRequired,
          ]).isRequired
        ).isRequired,
      }).isRequired,
    ]).isRequired
  ),
  /**
   * Used to set an id on the HTML `<input>` element.
   * If not used in FormField, either `aria-label`, `aria-labelledby`, or inputId associated with a `<label>` is required
   * for accessibility.
   */
  inputId: PropTypes.string,
  /**
   * Determines if input field can be cleared of its selected value.
   * If `true`, input field shows an X for deleting the field's contents and resetting it to the default or placeholder value
   */
  isClearable: PropTypes.bool,
  /**
   * Function that returns a promise, which is the set of options to display in the menu once the promise resolves.
   * This function should use the inputValue (which is passed to the function) to filter the array of options.
   * Will not be used if `options` are provided and component is being used synchronously.
   */
  loadAsyncOptions: PropTypes.func,
  /** Text to display when loading options (or a function returning the string to show). */
  loadingMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Text to display when there are no options to show in the menu (or a function returning the string to show). */
  noOptionsMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Function that takes a JS event as an argument. Called when the SingleSelect loses focus */
  onBlur: PropTypes.func,
  /**
   * Function that takes a fake event as an argument. This event contains a target object with value (map of keys to true/false) and id properties.
   * Called when the SingleSelect value changes
   */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the SingleSelect receives focus */
  onFocus: PropTypes.func,
  /**
   * Array of all options available.
   * Uses a prebuilt filter function which does a case insensitive string match using option value and text.
   * If you want to use different initial options or filtering, use `initialAsyncOptions` and `loadAsyncOptions` instead.
   * If provided, `loadAsyncOptions` and `initialAsyncOptions` props will not be used.
   */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
      PropTypes.shape({
        label: optionalStringValidator,
        options: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string.isRequired,
            PropTypes.shape({
              value: PropTypes.string.isRequired,
              disabled: optionalBoolValidator,
              label: optionalStringValidator,
            }).isRequired,
          ]).isRequired
        ).isRequired,
      }).isRequired,
    ]).isRequired
  ),
  /**
   * Text shown when no values are selected and the user is not typing.
   */
  placeholder: PropTypes.string,
  /** An instance of the react-select class-based component which underlies this
   * implementation.
   */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.shape({
      /** Note: { current: PropTypes.instanceOf(SingleSelectRef) } would be more
       *  correct, but causes a StackOverflow when building gatsby.
       */
      current: PropTypes.any,
    }).isRequired,
  ]) as Validator<Ref<SingleSelectRef>>,
  /** Indicates whether it is required to select at least one option. */
  required: PropTypes.bool,
  /** Select the currently focused option when the user presses Tab. */
  tabSelectsValue: PropTypes.bool,
  /**
   * Determines the selected options, and does not allow them to be updated internally ([controlled](https://reactjs.org/docs/forms.html#controlled-components))
   */
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          disabled: optionalBoolValidator,
          label: optionalStringValidator,
        }).isRequired,
      ]).isRequired
    ),
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
    ]),
  ]),
  /**
   * Props passed down to underlying [react-select Async component](https://react-select.com/props)
   *
   * Props from react-select that get swallowed:
   * * `classNamePrefix`
   * * `components`
   * * `defaultOptions` (use `initialAsyncOptions` instead)
   * * `getOptionLabel`
   * * `getOptionValue`
   * * `hideSelectedOptions` (hardcoded `false`)
   * * `isDisabled` (use `disabled` instead)
   * * `isMulti` (hardcoded `false`)
   * * `isOptionDisabled`
   * * `isSearchable` (hardcoded `true`)
   * * `loadOptions` (use `loadAsyncOptions` instead)
   * * `styles`
   */
  '...rest': PropTypes.any,
};
SingleSelectComponent.propTypes = singleSelectPropsTypes;

export default forwardRefToProps(SingleSelectComponent) as SingleSelectType;
